import React, { useEffect, useState } from "react";
import api from "../../api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { ShoppingCart, Eye, RefreshCw, Search, X, Globe, User, Calendar, CreditCard, ShieldCheck } from "lucide-react";

const STATUS_LABELS = {
  pending: { label: "Ausstehend", color: "bg-amber-100 text-amber-800 border-amber-200" },
  processing: { label: "In Bearbeitung", color: "bg-blue-100 text-blue-800 border-blue-200" },
  paid: { label: "Bezahlt", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  active: { label: "Aktiv", color: "bg-emerald-500 text-white border-emerald-600" },
  completed: { label: "Abgeschlossen", color: "bg-slate-100 text-slate-800 border-slate-300" },
  cancelled: { label: "Storniert", color: "bg-red-100 text-red-800 border-red-200" },
  expired: { label: "Abgelaufen", color: "bg-slate-200 text-slate-600 border-slate-300" },
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/orders");
      setOrders(res.data || []);
    } catch (err) {
      console.error("Orders error:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}`, { status });
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status } : o))
      );
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder((prev) => ({ ...prev, status }));
      }
    } catch (err) {
      alert("Fehler beim Aktualisieren: " + (err.response?.data?.detail || err.message));
    }
  };

  const filteredOrders = orders.filter((o) => {
    if (statusFilter !== "all" && o.status !== statusFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const match =
        (o.id || "").toLowerCase().includes(q) ||
        (o.reference || "").toLowerCase().includes(q) ||
        (o.userName || "").toLowerCase().includes(q) ||
        (o.productName || "").toLowerCase().includes(q) ||
        (o.domainName || "").toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="admin-orders-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0f172a]">Bestellungen</h1>
          <p className="text-slate-500 mt-1">
            Übersicht aller eingehenden Kundenbestellungen für Webhosting und Server
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchOrders}
            className="flex items-center gap-1.5"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            <span>Aktualisieren</span>
          </Button>
          <Badge variant="secondary" className="text-sm px-3 py-1 font-bold">
            {orders.length} Gesamt
          </Badge>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center justify-between gap-3 flex-wrap">
        <div className="relative grow max-w-md">
          <Search size={15} className="absolute left-3 top-2.5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen nach Referenz, Kunde, Produkt, Domain..."
            className="w-full pl-9 pr-4 py-1.5 rounded-lg border border-slate-200 text-sm focus:border-[#1E88E5] focus:outline-none"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-slate-500 uppercase">Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm bg-white font-medium focus:border-[#1E88E5] focus:outline-none"
          >
            <option value="all">Alle Status</option>
            <option value="pending">Ausstehend</option>
            <option value="processing">In Bearbeitung</option>
            <option value="paid">Bezahlt</option>
            <option value="active">Aktiv</option>
            <option value="completed">Abgeschlossen</option>
            <option value="cancelled">Storniert</option>
          </select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && orders.length === 0 ? (
            <div className="p-12 text-center text-slate-500">Bestellungen werden geladen...</div>
          ) : filteredOrders.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              Keine Bestellungen vorhanden.
            </div>
          ) : (
            <>
              {/* MOBILE CARDS VIEW (Visible only on mobile screens < 768px) */}
              <div className="block md:hidden divide-y divide-slate-100 p-2 space-y-3">
                {filteredOrders.map((order) => (
                  <div
                    key={order.id}
                    className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-black text-sm text-[#0f172a]">
                        #{order.contractNumber || order.reference || order.id?.slice(0, 8)}
                      </span>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${STATUS_LABELS[order.status]?.color || "bg-slate-100 text-slate-700"}`}>
                        {STATUS_LABELS[order.status]?.label || order.status}
                      </span>
                    </div>

                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-slate-400 font-semibold">Kunde:</span>
                        <span className="font-bold text-slate-800">{order.userName || "Kunde"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400 font-semibold">Paket:</span>
                        <span className="font-bold text-slate-900">{order.productName || "Hosting"}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 font-semibold">Domain:</span>
                        <span className="font-mono font-bold text-[#1E88E5]">
                          {order.domainName || "Später"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 font-semibold">Zahlung:</span>
                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-md border ${
                          (order.paymentMethod || "").toLowerCase() === "twint"
                            ? "bg-amber-50 text-amber-800 border-amber-300 font-bold"
                            : "bg-slate-100 text-slate-700 border-slate-200"
                        }`}>
                          {order.paymentMethod ? order.paymentMethod.toUpperCase() : "TWINT"}
                        </span>
                      </div>
                      <div className="flex justify-between items-baseline pt-1">
                        <span className="text-slate-400 font-semibold">Betrag:</span>
                        <span className="font-mono font-black text-sm text-slate-900">
                          CHF {Number(order.total || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                      <Select
                        value={order.status}
                        onValueChange={(value) => updateOrderStatus(order.id, value)}
                      >
                        <SelectTrigger className="h-8 text-xs font-bold flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Ausstehend</SelectItem>
                          <SelectItem value="processing">In Bearbeitung</SelectItem>
                          <SelectItem value="paid">Bezahlt</SelectItem>
                          <SelectItem value="active">Aktiv</SelectItem>
                          <SelectItem value="completed">Abgeschlossen</SelectItem>
                          <SelectItem value="cancelled">Storniert</SelectItem>
                          <SelectItem value="expired">Abgelaufen</SelectItem>
                        </SelectContent>
                      </Select>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedOrder(order)}
                        className="h-8 text-xs font-bold flex items-center gap-1"
                      >
                        <Eye size={13} />
                        <span>Details</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* DESKTOP TABLE VIEW (Visible on >= 768px screens) */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bestellung / Vertrag #</TableHead>
                      <TableHead>Kunde</TableHead>
                      <TableHead>Produkt / Paket</TableHead>
                      <TableHead>Domain</TableHead>
                      <TableHead>Zahlung</TableHead>
                      <TableHead>Laufzeit</TableHead>
                      <TableHead>Betrag</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Datum</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((order) => (
                      <TableRow key={order.id} className="hover:bg-slate-50 transition">
                        <TableCell className="font-mono font-bold text-[#0f172a]">
                          #{order.contractNumber || order.reference || order.id?.slice(0, 8)}
                        </TableCell>
                        <TableCell className="font-semibold text-slate-800">
                          {order.userName || "Kunde"}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium text-slate-900">{order.productName || "Hosting"}</span>
                        </TableCell>
                        <TableCell>
                          {order.domainName ? (
                            <span className="inline-flex items-center gap-1 text-xs font-mono font-bold text-[#1E88E5] bg-blue-50 px-2 py-0.5 rounded-md">
                              <Globe size={11} />
                              {order.domainName}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400 italic">Später</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-md border ${
                            (order.paymentMethod || "").toLowerCase() === "twint"
                              ? "bg-amber-50 text-amber-800 border-amber-300 font-bold"
                              : "bg-slate-100 text-slate-700 border-slate-200"
                          }`}>
                            {order.paymentMethod ? order.paymentMethod.toUpperCase() : "TWINT"}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs">
                          {order.duration === "yearly" ? (
                            <span className="font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                              Jährlich
                            </span>
                          ) : (
                            <span className="font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                              Monatlich
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono font-bold text-slate-900">
                          CHF {Number(order.total || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={order.status}
                            onValueChange={(value) => updateOrderStatus(order.id, value)}
                          >
                            <SelectTrigger className="w-32 h-8 text-xs font-bold">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Ausstehend</SelectItem>
                              <SelectItem value="processing">In Bearbeitung</SelectItem>
                              <SelectItem value="paid">Bezahlt</SelectItem>
                              <SelectItem value="active">Aktiv</SelectItem>
                              <SelectItem value="completed">Abgeschlossen</SelectItem>
                              <SelectItem value="cancelled">Storniert</SelectItem>
                              <SelectItem value="expired">Abgelaufen</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                          {new Date(order.createdAt).toLocaleDateString("de-CH", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedOrder(order)}
                            title="Bestelldetails ansehen"
                            className="h-8 w-8 p-0"
                          >
                            <Eye className="h-4 w-4 text-slate-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-4 backdrop-blur-xs"
          onClick={() => setSelectedOrder(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-xl w-full p-4 sm:p-6 shadow-2xl space-y-4 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-slate-200">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#E63946]">
                  BESTELLUNGSDETAILS
                </span>
                <h3 className="text-lg sm:text-xl font-black text-[#0f172a]">
                  #{selectedOrder.contractNumber || selectedOrder.reference || selectedOrder.id}
                </h3>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm bg-slate-50 p-4 rounded-xl border border-slate-200">
              <div>
                <span className="block text-[11px] font-bold text-slate-400 uppercase">Kunde</span>
                <span className="font-bold text-[#0f172a]">{selectedOrder.userName || "Kunde"}</span>
              </div>
              <div>
                <span className="block text-[11px] font-bold text-slate-400 uppercase">Produkt</span>
                <span className="font-bold text-[#0f172a]">{selectedOrder.productName}</span>
              </div>
              <div>
                <span className="block text-[11px] font-bold text-slate-400 uppercase">Domain</span>
                <span className="font-mono font-bold text-[#1E88E5]">
                  {selectedOrder.domainName || "Keine Domain angegeben"}
                </span>
              </div>
              <div>
                <span className="block text-[11px] font-bold text-slate-400 uppercase">Zahlungsmethode</span>
                <span className="font-bold text-slate-900">
                  {selectedOrder.paymentMethod ? selectedOrder.paymentMethod.toUpperCase() : "TWINT"}
                </span>
              </div>
              <div>
                <span className="block text-[11px] font-bold text-slate-400 uppercase">Abrechnung</span>
                <span className="font-semibold text-slate-800">
                  {selectedOrder.duration === "yearly" ? "Jährlich (10% Rabatt)" : "Monatlich"}
                </span>
              </div>
              <div>
                <span className="block text-[11px] font-bold text-slate-400 uppercase">Vertragsnummer</span>
                <span className="font-mono font-bold text-[#D4AF37]">
                  {selectedOrder.contractNumber || selectedOrder.reference || "RW-AUTO"}
                </span>
              </div>
            </div>

            {/* TWINT Details if selected */}
            {(selectedOrder.paymentMethod || "").toLowerCase() === "twint" && (
              <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900 space-y-1">
                <span className="font-black text-amber-900 flex items-center gap-1.5">
                  <CreditCard size={14} className="text-amber-700" />
                  <span>TWINT Zahlungsinfo:</span>
                </span>
                <p>
                  Kunde überweist via TWINT an <strong>076 610 61 08</strong> mit Referenz{" "}
                  <strong>{selectedOrder.contractNumber || selectedOrder.reference}</strong>.
                </p>
              </div>
            )}

            <div className="border border-slate-200 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between text-slate-600 text-xs sm:text-sm">
                <span>Zwischensumme:</span>
                <span className="font-mono">CHF {Number(selectedOrder.subtotal || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-slate-600 text-xs sm:text-sm">
                <span>Schweizer MwSt (8.1%):</span>
                <span className="font-mono">CHF {Number(selectedOrder.vatAmount || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm sm:text-base font-extrabold text-[#0f172a] pt-2 border-t border-slate-200">
                <span>Gesamtbetrag:</span>
                <span className="font-mono text-[#E63946]">CHF {Number(selectedOrder.total || 0).toFixed(2)}</span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-500 uppercase">Status:</span>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${STATUS_LABELS[selectedOrder.status]?.color || "bg-slate-100 text-slate-700"}`}>
                  {STATUS_LABELS[selectedOrder.status]?.label || selectedOrder.status}
                </span>
              </div>
              <Button onClick={() => setSelectedOrder(null)} variant="outline">
                Schliessen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
